/**
 * Signal Mapper
 * Transforms WebSocket signal payload to Signal interface
 */

import { Signal } from "../views/Dashboard/Tables/components/SignalCard";

/** WebSocket trade_request message – FE contract (type === "trade_request") */
export interface WebSocketSignalPayload {
  type: "trade_request";
  data: {
    /** Backend event identifier used for execute endpoint */
    event_id: string;
    instrument: string;
    side: "BUY" | "SELL";
    quantity: string;
    order_type: string;
    time_in_force: string;
    limit_price: string | null;
    stop_price: string | null;
    risk_budget_pct: number | null;
    /** Optional pre-computed RR from backend (numeric) */
    risk_reward_ratio?: number | null;
    validation_status: string;
    validation_errors: unknown[];
    trade_metadata: {
      entry_price: string;
      stop_loss_price: string;
      take_profit_price: string;
      timeframe: string | null;
      correlation_id: string | null;
      /** Optional gate context from RawTradeProposal.regime_context (shadow bot) */
      regime_context?: {
        gate_used?: string | null;
        gate_rr?: number | null;
      } | null;
    };
    timestamp: string; // ISO UTC
  };
  timestamp: string; // ISO UTC
}

/**
 * Format instrument symbol from "XAUUSD" to "XAU/USD"
 * Inserts "/" before the last 3-4 characters (USD, USDT, etc.)
 */
export const formatInstrumentSymbol = (instrument: string): string => {
  // Common quote currencies to look for
  const quoteCurrencies = ["USD", "USDT", "EUR", "GBP", "JPY", "AUD", "CAD", "CHF", "NZD"];
  
  for (const quote of quoteCurrencies) {
    if (instrument.endsWith(quote)) {
      const base = instrument.slice(0, -quote.length);
      return `${base}/${quote}`;
    }
  }
  
  // If no match, try to insert "/" before last 3-4 characters as fallback
  if (instrument.length >= 6) {
    const lastPart = instrument.slice(-4);
    const firstPart = instrument.slice(0, -4);
    return `${firstPart}/${lastPart}`;
  }
  
  // If too short, return as-is
  return instrument;
};

/**
 * Format price string with appropriate decimals
 * Preserves decimals from input string
 */
export const formatPrice = (price: string): string => {
  const numPrice = parseFloat(price);
  if (isNaN(numPrice)) {
    return price; // Return original if not a valid number
  }

  // Format based on price magnitude
  if (numPrice >= 1000) {
    return numPrice.toLocaleString("en-US", { maximumFractionDigits: 0 });
  } else if (numPrice >= 1) {
    return numPrice.toLocaleString("en-US", { maximumFractionDigits: 2 });
  } else {
    return numPrice.toFixed(4);
  }
};

/**
 * Calculate risk/reward ratio from entry, TP, and SL prices
 * Returns formatted as "1:X.X"
 */
export const calculateRiskReward = (
  entry: number,
  tp: number,
  sl: number,
  side: "BUY" | "SELL"
): string => {
  let rr: number;

  if (side === "BUY") {
    // For LONG: RR = (TP - entry) / (entry - SL)
    const profit = tp - entry;
    const risk = entry - sl;
    rr = risk > 0 ? profit / risk : 0;
  } else {
    // For SHORT: RR = (entry - TP) / (SL - entry)
    const profit = entry - tp;
    const risk = sl - entry;
    rr = risk > 0 ? profit / risk : 0;
  }

  // Format as "1:X.X"
  return `1:${rr.toFixed(1)}`;
};

/**
 * Calculate expiry time from createdAt timestamp
 * Adds 10 minutes to createdAt (dev only - will be refined later)
 */
export const calculateExpiryTime = (createdAt: Date): Date => {
  const expiryTime = new Date(createdAt);
  expiryTime.setMinutes(expiryTime.getMinutes() + 10);
  return expiryTime;
};

/**
 * Map WebSocket signal payload to Signal interface
 */
export const mapWebSocketSignalToSignal = (payload: WebSocketSignalPayload): Signal => {
  const { data } = payload;
  
  // Parse prices
  const entryPrice = parseFloat(data.trade_metadata.entry_price);
  const tpPrice = parseFloat(data.trade_metadata.take_profit_price);
  const slPrice = parseFloat(data.trade_metadata.stop_loss_price);
  
  // Parse timestamp
  const createdAt = new Date(data.timestamp);
  
  // Map side to type
  const type = data.side === "BUY" ? "LONG" : "SHORT";
  
  // Format symbol (e.g., "XAUUSD" -> "XAU/USD")
  // Store formatted symbol in a way we can access it later for matching
  const formattedSymbol = formatInstrumentSymbol(data.instrument);
  
  // Format prices
  const entry = formatPrice(data.trade_metadata.entry_price);
  const tp = formatPrice(data.trade_metadata.take_profit_price);
  const sl = formatPrice(data.trade_metadata.stop_loss_price);
  
  // Calculate risk/reward – prefer backend-provided numeric RR when present
  const backendRr = typeof data.risk_reward_ratio === "number"
    ? data.risk_reward_ratio
    : null;
  const rr = backendRr != null
    ? `1:${backendRr.toFixed(2)}`
    : calculateRiskReward(entryPrice, tpPrice, slPrice, data.side);

  // Optional gate context (strategy gate + dynamic RR override)
  const gateUsed =
    data.trade_metadata.regime_context?.gate_used ?? null;
  const gateRr =
    typeof data.trade_metadata.regime_context?.gate_rr === "number"
      ? data.trade_metadata.regime_context!.gate_rr
      : null;
  
  // Calculate expiry time (10 minutes from createdAt - dev only)
  const expiryTime = calculateExpiryTime(createdAt);
  
  // Stable id for deduplication (correlation_id when present, else timestamp+instrument+entry)
  const id =
    data.trade_metadata.correlation_id ??
    `${data.timestamp}-${data.instrument}-${data.trade_metadata.entry_price}`;
  
  return {
    id,
    // Backend execution id (used for POST /signals/{event_id}/execute)
    eventId: data.event_id,
    type,
    entry,
    exit: undefined, // Not provided in WebSocket payload
    tp,
    sl,
    rr,
    pp: undefined, // Will be calculated in UI if needed
    ppWithROI: undefined, // Will be calculated in UI if needed
    expiryTime,
    createdAt,
    reasoning: undefined, // Not provided in WebSocket payload
    symbol: formattedSymbol,
    // Initial numeric RR from backend (used by modal; can be refined by calculate-risk endpoint)
    initialRiskRewardRatio: backendRr,
    gateUsed,
    gateRr,
  };
};
